import { useHover, usePress } from '@react-aria/interactions'
import { useSelector } from 'react-redux'
import { ArticleMeta, RootState } from 'types'
import { find, isEqual } from 'lodash'
import { Favorite } from 'api/clients/FavoriteClient'
import { useCreateFavorite } from 'hooks/queries/favorites/useCreateFavorite'
import { useDeleteFavorite } from 'hooks/queries/favorites/useDeleteFavorite'
import { Star } from 'blaze-ui/icons'
import { mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import Styles from './FavoriteButton.module.scss'
import { useFocusRing } from '@react-aria/focus'

export const FavoriteButton = ({ article, isVisible = true }: { article: ArticleMeta; isVisible?: boolean }) => {
  const favorite = useSelector(
    ({ favorites }: RootState) =>
      find(favorites, {
        favoritable_id: article.id,
      }) as Favorite | undefined,
    isEqual,
  )
  const { mutate: createFavorite } = useCreateFavorite()
  const { mutate: deleteFavorite } = useDeleteFavorite()

  const handleFavorite = () => {
    if (favorite) {
      deleteFavorite({ id: favorite.id, title: favorite.favoritable_title })
    } else {
      createFavorite({
        favoritableId: article.id,
        favoritableType: 'article',
        favoritableTitle: article.title,
      })
    }
  }

  const { isPressed, pressProps } = usePress({ onPress: handleFavorite })
  const { isHovered, hoverProps } = useHover({})
  const { isFocused, focusProps } = useFocusRing({})

  return (
    <div
      className={cx(Styles.favoriteButton, {
        [Styles.favorite]: !!favorite,
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocused,
        [Styles.visible]: isVisible || !!favorite,
      })}
      role="button"
      tabIndex={0}
      aria-label={favorite ? 'Unfavorite' : 'Favorite'}
      {...mergeProps(pressProps, hoverProps, focusProps)}
    >
      <Star />
    </div>
  )
}
